// src/pages/QboSyncPage.js
// QuickBooks Online sync page — Customer sync (Phase 2), Product sync (Phase 3).

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import qboService from '../services/qboService';
import SearchableSelect from '../components/SearchableSelect';

// ── Helpers ───────────────────────────────────────────────────────────────────

const initSelections = (newInQbo) =>
  Object.fromEntries(newInQbo.map(({ qbo }) => [qbo.qbCustomerId, false]));

// ── Sub-components ────────────────────────────────────────────────────────────

function CustomerSyncTab() {
  const [loading, setLoading]       = useState(false);
  const [applying, setApplying]     = useState(false);
  const [preview, setPreview]       = useState(null);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [pushingId, setPushingId]   = useState(null);

  // Per-conflict choice: 'keep' | 'use_qbo' | object of field choices
  const [conflictChoices, setConflictChoices] = useState({});
  // Which "new in QBO" customers to import
  const [importSelections, setImportSelections] = useState({});

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const data = await qboService.previewCustomers();
      setPreview(data);
      setImportSelections(initSelections(data.newInQbo || []));
      const choices = {};
      (data.conflicts || []).forEach(({ qbo }) => { choices[qbo.qbCustomerId] = 'keep'; });
      setConflictChoices(choices);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleApply = async () => {
    setApplying(true);
    setError('');
    setSuccess('');
    try {
      const link = (preview.matched || [])
        .filter(m => !m.quicklog.qbCustomerId)   // only those not yet linked
        .map(m => ({ quicklogId: m.quicklog.id, qbCustomerId: m.qbo.qbCustomerId }));

      const overwrite = (preview.conflicts || [])
        .filter(c => conflictChoices[c.qbo.qbCustomerId] === 'use_qbo')
        .map(c => ({
          quicklogId:   c.quicklog.id,
          qbCustomerId: c.qbo.qbCustomerId,
          fields:       c.diffFields || ['email'],
        }));

      // conflicts with 'keep' just get linked (no field overwrite)
      const linkConflicts = (preview.conflicts || [])
        .filter(c => conflictChoices[c.qbo.qbCustomerId] === 'keep')
        .map(c => ({ quicklogId: c.quicklog.id, qbCustomerId: c.qbo.qbCustomerId }));

      const importList = Object.entries(importSelections)
        .filter(([, selected]) => selected)
        .map(([qbCustomerId]) => ({ qbCustomerId }));

      const result = await qboService.applyCustomers({
        link:     [...link, ...linkConflicts],
        import:   importList,
        overwrite,
      });

      const msg = [
        result.linked      && `${result.linked} linked`,
        result.imported    && `${result.imported} imported`,
        result.overwritten && `${result.overwritten} updated`,
      ].filter(Boolean).join(', ');

      setSuccess(`Sync applied${msg ? ': ' + msg : ''}. Refreshing…`);
      setTimeout(() => loadPreview(), 1500);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to apply sync');
    } finally {
      setApplying(false);
    }
  };

  const handlePush = async (quicklogId) => {
    setPushingId(quicklogId);
    setError('');
    setSuccess('');
    try {
      await qboService.pushCustomerToQbo(quicklogId);
      setSuccess('Pushed to QuickBooks. Refreshing…');
      setTimeout(() => loadPreview(), 1000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to push to QuickBooks');
    } finally {
      setPushingId(null);
    }
  };

  const selectedImportCount = Object.values(importSelections).filter(Boolean).length;
  const pendingMatchCount = (preview?.matched || []).filter(m => !m.quicklog.qbCustomerId).length;
  const hasAnything = pendingMatchCount > 0 ||
    (preview?.conflicts || []).length > 0 ||
    selectedImportCount > 0;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0">Customer Sync</h5>
          <small className="text-muted">Match quickLOG customers with QuickBooks Online customers</small>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={loadPreview} disabled={loading}>
          {loading ? <span className="spinner-border spinner-border-sm" /> : 'Refresh'}
        </button>
      </div>

      {error && <div className="alert alert-danger py-2 px-3" style={{ fontSize: 13 }}>{error}</div>}
      {success && <div className="alert alert-success py-2 px-3" style={{ fontSize: 13 }}>{success}</div>}

      {loading && (
        <div className="text-center py-5 text-muted">
          <span className="spinner-border spinner-border-sm me-2" />Loading QBO customers…
        </div>
      )}

      {preview && !loading && (
        <>
          {/* ── Auto-matched ── */}
          <Section
            title="Auto-matched"
            badge={preview.matched?.length || 0}
            badgeColor="success"
            hint="These will be linked automatically when you apply."
            defaultOpen
          >
            {(preview.matched || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No matches found.</p>
            ) : (
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>quickLOG</th>
                    <th>QBO</th>
                    <th>Match via</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.matched.map(m => (
                    <tr key={m.qbo.qbCustomerId}>
                      <td>{m.quicklog.name}</td>
                      <td>{m.qbo.name}</td>
                      <td className="text-muted">{m.matchReason || 'ID'}</td>
                      <td>
                        {m.quicklog.qbCustomerId
                          ? <span className="badge bg-success">Already linked</span>
                          : <span className="badge bg-primary">Will link</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* ── Conflicts ── */}
          <Section
            title="Conflicts"
            badge={preview.conflicts?.length || 0}
            badgeColor="warning"
            hint="Same name found but with different data. Choose which version to keep."
            defaultOpen={(preview.conflicts || []).length > 0}
          >
            {(preview.conflicts || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No conflicts.</p>
            ) : (
              <div className="d-flex flex-column gap-3">
                {preview.conflicts.map(c => (
                  <div key={c.qbo.qbCustomerId} className="border rounded p-3" style={{ fontSize: 13 }}>
                    <div className="row g-3 mb-2">
                      <div className="col-md-5">
                        <div className="fw-semibold mb-1 text-muted" style={{ fontSize: 11 }}>QUICKLOG</div>
                        <div><strong>{c.quicklog.name}</strong></div>
                        <div className="text-muted">{c.quicklog.email || '—'}</div>
                        <div className="text-muted">{c.quicklog.address || '—'}</div>
                      </div>
                      <div className="col-md-2 d-flex align-items-center justify-content-center">
                        <span className="text-muted">⟷</span>
                      </div>
                      <div className="col-md-5">
                        <div className="fw-semibold mb-1 text-muted" style={{ fontSize: 11 }}>QBO</div>
                        <div><strong>{c.qbo.name}</strong></div>
                        <div className="text-muted">{c.qbo.email || '—'}</div>
                        <div className="text-muted">{c.qbo.address || '—'}</div>
                      </div>
                    </div>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          id={`keep-${c.qbo.qbCustomerId}`}
                          checked={conflictChoices[c.qbo.qbCustomerId] === 'keep'}
                          onChange={() => setConflictChoices(p => ({ ...p, [c.qbo.qbCustomerId]: 'keep' }))}
                        />
                        <label className="form-check-label" htmlFor={`keep-${c.qbo.qbCustomerId}`}>
                          Keep quickLOG data (just link IDs)
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          id={`qbo-${c.qbo.qbCustomerId}`}
                          checked={conflictChoices[c.qbo.qbCustomerId] === 'use_qbo'}
                          onChange={() => setConflictChoices(p => ({ ...p, [c.qbo.qbCustomerId]: 'use_qbo' }))}
                        />
                        <label className="form-check-label" htmlFor={`qbo-${c.qbo.qbCustomerId}`}>
                          Use QBO data (overwrite {(c.diffFields || []).join(', ')})
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* ── New in QBO ── */}
          <Section
            title="New in QBO"
            badge={preview.newInQbo?.length || 0}
            badgeColor="info"
            hint="These customers exist in QBO but not in quickLOG. Select which ones to import."
            defaultOpen={(preview.newInQbo || []).length > 0}
          >
            {(preview.newInQbo || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No new QBO customers to import.</p>
            ) : (
              <>
                <div className="d-flex gap-2 mb-2">
                  <button
                    className="btn btn-xs btn-outline-secondary"
                    style={{ fontSize: 12, padding: '2px 8px' }}
                    onClick={() => setImportSelections(Object.fromEntries(preview.newInQbo.map(({ qbo }) => [qbo.qbCustomerId, true])))}
                  >
                    Select all
                  </button>
                  <button
                    className="btn btn-xs btn-outline-secondary"
                    style={{ fontSize: 12, padding: '2px 8px' }}
                    onClick={() => setImportSelections(initSelections(preview.newInQbo))}
                  >
                    Deselect all
                  </button>
                </div>
                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr><th style={{ width: 36 }}></th><th>Name</th><th>Email</th><th>Phone</th></tr>
                  </thead>
                  <tbody>
                    {preview.newInQbo.map(({ qbo }) => (
                      <tr key={qbo.qbCustomerId}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!importSelections[qbo.qbCustomerId]}
                            onChange={e => setImportSelections(p => ({ ...p, [qbo.qbCustomerId]: e.target.checked }))}
                          />
                        </td>
                        <td>{qbo.name}</td>
                        <td className="text-muted">{qbo.email || '—'}</td>
                        <td className="text-muted">{qbo.phone || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </Section>

          {/* ── Unlinked (info only) ── */}
          {(preview.unlinked || []).length > 0 && (
            <Section
              title="Not in QBO"
              badge={preview.unlinked.length}
              badgeColor="secondary"
              hint="These quickLOG customers have no QBO link. Push them to create a real QBO customer, or wait to be matched automatically."
              defaultOpen={false}
            >
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead><tr><th>Name</th><th>Email</th><th></th></tr></thead>
                <tbody>
                  {preview.unlinked.map(({ quicklog: ql }) => (
                    <tr key={ql.id}>
                      <td>{ql.name}</td>
                      <td className="text-muted">{ql.email || '—'}</td>
                      <td className="text-end">
                        <button
                          className="btn btn-xs btn-outline-success"
                          style={{ fontSize: 12, padding: '2px 8px' }}
                          disabled={pushingId === ql.id}
                          onClick={() => handlePush(ql.id)}
                        >
                          {pushingId === ql.id ? <span className="spinner-border spinner-border-sm" /> : 'Push to QBO'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {/* ── Apply button ── */}
          <div className="mt-4 d-flex align-items-center gap-3">
            <button
              className="btn btn-primary"
              onClick={handleApply}
              disabled={applying || !hasAnything}
            >
              {applying
                ? <><span className="spinner-border spinner-border-sm me-2" />Applying…</>
                : `Apply Sync${selectedImportCount > 0 ? ` (import ${selectedImportCount})` : ''}`}
            </button>
            {!hasAnything && (
              <span className="text-muted" style={{ fontSize: 13 }}>Nothing to apply — all customers are already in sync.</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Product Sync Tab ──────────────────────────────────────────────────────────

function ProductSyncTab() {
  const [loading, setLoading]   = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview]   = useState(null);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  // Manual match overrides: priceListItemId → qbItemName (string)
  const [manualMatches, setManualMatches] = useState({});
  // Accepted suggestions: priceListItemId → true/false
  const [acceptedSuggestions, setAcceptedSuggestions] = useState({});
  // Re-link overrides for already-linked items: priceListItemId → new qbItemName (or '' to unlink)
  const [relinkMatches, setRelinkMatches] = useState({});

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const data = await qboService.previewItems();
      setPreview(data);
      // Default: accept all exact suggestions
      const accepted = {};
      (data.suggestions || []).forEach(s => {
        if (s.confidence === 'exact') accepted[s.priceListItem.id] = true;
      });
      setAcceptedSuggestions(accepted);
      setManualMatches({});
      setRelinkMatches({});
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleApply = async () => {
    setApplying(true);
    setError('');
    setSuccess('');
    try {
      const link = [];

      // Build name → id lookup from the full QBO items list
      const qboById = new Map((preview.qboItems || []).map(qi => [qi.name, qi.id]));

      // Accepted suggestions — suggestedQbItem already has id
      (preview.suggestions || []).forEach(s => {
        if (acceptedSuggestions[s.priceListItem.id]) {
          link.push({
            priceListItemId: s.priceListItem.id,
            qbItemName: s.suggestedQbItem.name,
            qbItemId:   s.suggestedQbItem.id || qboById.get(s.suggestedQbItem.name) || null,
          });
        }
      });

      // Manual matches — resolve id from name
      (preview.unmatched || []).forEach(u => {
        const chosen = manualMatches[u.priceListItem.id];
        if (chosen) link.push({
          priceListItemId: u.priceListItem.id,
          qbItemName: chosen,
          qbItemId:   qboById.get(chosen) || null,
        });
      });

      // Re-linked already-linked items (changed or unlinked)
      Object.entries(relinkMatches).forEach(([id, newName]) => {
        link.push({
          priceListItemId: Number(id),
          qbItemName: newName || null,
          qbItemId:   newName ? (qboById.get(newName) || null) : null,
        });
      });

      if (link.length === 0) {
        setError('Nothing to apply — accept a suggestion, choose a manual match, or change a linked item.');
        setApplying(false);
        return;
      }

      const result = await qboService.applyItems({ link });
      setSuccess(`Done: ${result.linked} item(s) updated.${result.errors?.length ? ` ${result.errors.length} error(s).` : ''}`);
      setTimeout(() => loadPreview(), 1500);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to apply sync');
    } finally {
      setApplying(false);
    }
  };

  const pendingCount =
    (preview?.suggestions || []).filter(s => acceptedSuggestions[s.priceListItem.id]).length +
    Object.values(manualMatches).filter(Boolean).length +
    Object.keys(relinkMatches).length;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0">Product / Item Sync</h5>
          <small className="text-muted">Link quickLOG price list items to QuickBooks Online items</small>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={loadPreview} disabled={loading}>
          {loading ? <span className="spinner-border spinner-border-sm" /> : 'Refresh'}
        </button>
      </div>

      {error && <div className="alert alert-danger py-2 px-3" style={{ fontSize: 13 }}>{error}</div>}
      {success && <div className="alert alert-success py-2 px-3" style={{ fontSize: 13 }}>{success}</div>}

      {loading && (
        <div className="text-center py-5 text-muted">
          <span className="spinner-border spinner-border-sm me-2" />Loading QBO items…
        </div>
      )}

      {preview && !loading && (
        <>
          {/* ── Already linked ── */}
          <Section
            title="Already linked"
            badge={preview.linked?.length || 0}
            badgeColor="success"
            hint="These price list items are linked. Use the dropdown to change or remove the link."
            defaultOpen={(preview.linked || []).length > 0}
          >
            {(preview.linked || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>None linked yet.</p>
            ) : (
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead><tr><th>quickLOG item</th><th>QBO item</th></tr></thead>
                <tbody>
                  {preview.linked.map(l => {
                    const currentVal = l.priceListItem.id in relinkMatches
                      ? relinkMatches[l.priceListItem.id]
                      : l.qbItemName;
                    const changed = l.priceListItem.id in relinkMatches;
                    return (
                      <tr key={l.priceListItem.id}>
                        <td>{l.priceListItem.Product?.name || l.priceListItem.presentationLabel}</td>
                        <td>
                          <SearchableSelect
                            size="sm"
                            style={{ maxWidth: 260 }}
                            className={changed ? 'border-warning' : ''}
                            placeholder="— unlink —"
                            value={currentVal || ''}
                            options={[
                              { value: '', label: '— unlink —' },
                              ...(preview.qboItems || []).map(qi => ({ value: qi.name, label: `${qi.name} (${qi.type})` })),
                            ]}
                            onChange={val => {
                              if (val === l.qbItemName) {
                                // Reverted to original — remove override
                                setRelinkMatches(p => { const n = { ...p }; delete n[l.priceListItem.id]; return n; });
                              } else {
                                setRelinkMatches(p => ({ ...p, [l.priceListItem.id]: val }));
                              }
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Section>

          {/* ── Suggestions ── */}
          <Section
            title="Suggested matches"
            badge={preview.suggestions?.length || 0}
            badgeColor="primary"
            hint="Name-based matches — confirm or reject each one."
            defaultOpen={(preview.suggestions || []).length > 0}
          >
            {(preview.suggestions || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No suggestions.</p>
            ) : (
              <>
                <div className="d-flex gap-2 mb-2">
                  <button
                    className="btn btn-outline-secondary"
                    style={{ fontSize: 12, padding: '2px 8px' }}
                    onClick={() => setAcceptedSuggestions(Object.fromEntries((preview.suggestions || []).map(s => [s.priceListItem.id, true])))}
                  >
                    Accept all
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    style={{ fontSize: 12, padding: '2px 8px' }}
                    onClick={() => setAcceptedSuggestions({})}
                  >
                    Reject all
                  </button>
                </div>
                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}></th>
                      <th>quickLOG item</th>
                      <th>QBO item</th>
                      <th>Type</th>
                      <th>Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.suggestions.map(s => (
                      <tr key={s.priceListItem.id}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!acceptedSuggestions[s.priceListItem.id]}
                            onChange={e => setAcceptedSuggestions(p => ({ ...p, [s.priceListItem.id]: e.target.checked }))}
                          />
                        </td>
                        <td>{s.priceListItem.Product?.name || s.priceListItem.presentationLabel}</td>
                        <td><code style={{ fontSize: 12 }}>{s.suggestedQbItem.name}</code></td>
                        <td className="text-muted">{s.suggestedQbItem.type}</td>
                        <td>
                          <span className={`badge ${s.confidence === 'exact' ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: 10 }}>
                            {s.confidence}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </Section>

          {/* ── Manual match ── */}
          <Section
            title="Manual match"
            badge={preview.unmatched?.length || 0}
            badgeColor="warning"
            hint="No automatic match found — select the QBO item for each."
            defaultOpen={(preview.unmatched || []).length > 0}
          >
            {(preview.unmatched || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>All items have a match.</p>
            ) : (
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead>
                  <tr><th>quickLOG item</th><th>QBO item</th></tr>
                </thead>
                <tbody>
                  {preview.unmatched.map(u => (
                    <tr key={u.priceListItem.id}>
                      <td>{u.priceListItem.Product?.name || u.priceListItem.presentationLabel}</td>
                      <td>
                        <SearchableSelect
                          size="sm"
                          style={{ maxWidth: 260 }}
                          placeholder="— skip —"
                          value={manualMatches[u.priceListItem.id] || ''}
                          options={[
                            { value: '', label: '— skip —' },
                            ...(preview.qboItems || []).map(qi => ({ value: qi.name, label: `${qi.name} (${qi.type})` })),
                          ]}
                          onChange={val => setManualMatches(p => ({ ...p, [u.priceListItem.id]: val }))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* ── Not in any price list (info) ── */}
          {(preview.notInPriceList || []).length > 0 && (
            <Section
              title="Not in any price list"
              badge={preview.notInPriceList.length}
              badgeColor="secondary"
              hint="These products exist in your catalog but aren't on any price list — add them to a price list to enable QBO linking."
              defaultOpen={false}
            >
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead><tr><th>Product</th><th>SKU</th></tr></thead>
                <tbody>
                  {preview.notInPriceList.map(p => (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td className="text-muted">{p.sku || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {/* ── New in QBO (info) ── */}
          {(preview.newInQbo || []).length > 0 && (
            <Section
              title="In QBO only"
              badge={preview.newInQbo.length}
              badgeColor="secondary"
              hint="QBO items not referenced by any price list item. No action needed."
              defaultOpen={false}
            >
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead><tr><th>QBO item name</th><th>Type</th></tr></thead>
                <tbody>
                  {preview.newInQbo.map(qi => (
                    <tr key={qi.id}>
                      <td><code style={{ fontSize: 12 }}>{qi.name}</code></td>
                      <td className="text-muted">{qi.type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {/* ── Apply ── */}
          <div className="mt-4 d-flex align-items-center gap-3">
            <button
              className="btn btn-primary"
              onClick={handleApply}
              disabled={applying || pendingCount === 0}
            >
              {applying
                ? <><span className="spinner-border spinner-border-sm me-2" />Applying…</>
                : `Apply Sync${pendingCount > 0 ? ` (${pendingCount} item${pendingCount > 1 ? 's' : ''})` : ''}`}
            </button>
            {pendingCount === 0 && (
              <span className="text-muted" style={{ fontSize: 13 }}>Accept suggestions or choose manual matches to apply.</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function IngredientAccountSyncTab() {
  const [loading, setLoading]   = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview]   = useState(null);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  // Manual match overrides: ingredientId → qbAccountId (string)
  const [manualMatches, setManualMatches] = useState({});
  // Accepted suggestions: ingredientId → true/false
  const [acceptedSuggestions, setAcceptedSuggestions] = useState({});
  // Re-link overrides for already-linked ingredients: ingredientId → new qbAccountId (or '' to unlink)
  const [relinkMatches, setRelinkMatches] = useState({});
  // Account overrides for AI suggestions the user picked a different account for: ingredientId → qbAccountId
  const [suggestionOverrides, setSuggestionOverrides] = useState({});

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const data = await qboService.previewIngredientAccounts();
      setPreview(data);
      // Default: accept all high-confidence suggestions
      const accepted = {};
      (data.suggestions || []).forEach(s => {
        if (s.confidence === 'high') accepted[s.ingredient.id] = true;
      });
      setAcceptedSuggestions(accepted);
      setManualMatches({});
      setRelinkMatches({});
      setSuggestionOverrides({});
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleApply = async () => {
    setApplying(true);
    setError('');
    setSuccess('');
    try {
      const link = [];
      const accountById = new Map((preview.accounts || []).map(a => [String(a.id), a]));

      (preview.suggestions || []).forEach(s => {
        if (acceptedSuggestions[s.ingredient.id]) {
          const overrideId = suggestionOverrides[s.ingredient.id];
          const account = overrideId ? accountById.get(overrideId) : null;
          link.push({
            ingredientId: s.ingredient.id,
            qbExpenseAccountId: account ? account.id : s.suggestedAccount.id,
            qbExpenseAccountName: account ? account.name : s.suggestedAccount.name,
          });
        }
      });

      (preview.unmatched || []).forEach(u => {
        const chosen = manualMatches[u.ingredient.id];
        if (chosen) link.push({
          ingredientId: u.ingredient.id,
          qbExpenseAccountId: chosen,
          qbExpenseAccountName: accountById.get(chosen)?.name || null,
        });
      });

      Object.entries(relinkMatches).forEach(([id, newAccountId]) => {
        link.push({
          ingredientId: Number(id),
          qbExpenseAccountId: newAccountId || null,
          qbExpenseAccountName: newAccountId ? (accountById.get(newAccountId)?.name || null) : null,
        });
      });

      if (link.length === 0) {
        setError('Nothing to apply — accept a suggestion, choose a manual match, or change a linked ingredient.');
        setApplying(false);
        return;
      }

      const result = await qboService.applyIngredientAccounts({ link });
      setSuccess(`Done: ${result.linked} ingredient(s) updated.${result.errors?.length ? ` ${result.errors.length} error(s).` : ''}`);
      setTimeout(() => loadPreview(), 1500);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to apply sync');
    } finally {
      setApplying(false);
    }
  };

  const pendingCount =
    (preview?.suggestions || []).filter(s => acceptedSuggestions[s.ingredient.id]).length +
    Object.values(manualMatches).filter(Boolean).length +
    Object.keys(relinkMatches).length;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0">Ingredient / Expense Account Sync</h5>
          <small className="text-muted">Match ingredients to QBO expense/COGS accounts, for receipt-scan pushes (AI-matched)</small>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={loadPreview} disabled={loading}>
          {loading ? <span className="spinner-border spinner-border-sm" /> : 'Refresh'}
        </button>
      </div>

      {error && <div className="alert alert-danger py-2 px-3" style={{ fontSize: 13 }}>{error}</div>}
      {success && <div className="alert alert-success py-2 px-3" style={{ fontSize: 13 }}>{success}</div>}

      {loading && (
        <div className="text-center py-5 text-muted">
          <span className="spinner-border spinner-border-sm me-2" />Matching ingredients to QBO accounts with AI…
        </div>
      )}

      {preview && !loading && (
        <>
          {preview.notes && (
            <div className="alert alert-warning py-2 px-3" style={{ fontSize: 13 }}>{preview.notes}</div>
          )}

          {/* ── Already linked ── */}
          <Section
            title="Already linked"
            badge={preview.linked?.length || 0}
            badgeColor="success"
            hint="These ingredients are linked. Use the dropdown to change or remove the link."
            defaultOpen={false}
          >
            {(preview.linked || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>None linked yet.</p>
            ) : (
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead><tr><th>Ingredient</th><th>QBO account</th></tr></thead>
                <tbody>
                  {preview.linked.map(l => {
                    const currentVal = l.ingredient.id in relinkMatches
                      ? relinkMatches[l.ingredient.id]
                      : String(l.qbAccountId);
                    const changed = l.ingredient.id in relinkMatches;
                    return (
                      <tr key={l.ingredient.id}>
                        <td>{l.ingredient.name}</td>
                        <td>
                          <SearchableSelect
                            size="sm"
                            style={{ maxWidth: 280 }}
                            className={changed ? 'border-warning' : ''}
                            placeholder="— unlink —"
                            value={currentVal || ''}
                            options={[
                              { value: '', label: '— unlink —' },
                              ...(preview.accounts || []).map(a => ({ value: String(a.id), label: a.name })),
                            ]}
                            onChange={val => {
                              if (val === String(l.qbAccountId)) {
                                setRelinkMatches(p => { const n = { ...p }; delete n[l.ingredient.id]; return n; });
                              } else {
                                setRelinkMatches(p => ({ ...p, [l.ingredient.id]: val }));
                              }
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Section>

          {/* ── AI suggestions ── */}
          <Section
            title="AI-suggested matches"
            badge={preview.suggestions?.length || 0}
            badgeColor="primary"
            hint="Confirm or reject each one."
            defaultOpen={(preview.suggestions || []).length > 0}
          >
            {(preview.suggestions || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No suggestions.</p>
            ) : (
              <>
                <div className="d-flex gap-2 mb-2">
                  <button
                    className="btn btn-outline-secondary"
                    style={{ fontSize: 12, padding: '2px 8px' }}
                    onClick={() => setAcceptedSuggestions(Object.fromEntries((preview.suggestions || []).map(s => [s.ingredient.id, true])))}
                  >
                    Accept all
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    style={{ fontSize: 12, padding: '2px 8px' }}
                    onClick={() => setAcceptedSuggestions({})}
                  >
                    Reject all
                  </button>
                </div>
                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}></th>
                      <th>Ingredient</th>
                      <th>QBO account</th>
                      <th>Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.suggestions.map(s => {
                      const currentVal = s.ingredient.id in suggestionOverrides
                        ? suggestionOverrides[s.ingredient.id]
                        : String(s.suggestedAccount.id);
                      const changed = s.ingredient.id in suggestionOverrides;
                      return (
                        <tr key={s.ingredient.id}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={!!acceptedSuggestions[s.ingredient.id]}
                              onChange={e => setAcceptedSuggestions(p => ({ ...p, [s.ingredient.id]: e.target.checked }))}
                            />
                          </td>
                          <td>{s.ingredient.name}</td>
                          <td>
                            <SearchableSelect
                              size="sm"
                              style={{ maxWidth: 280 }}
                              className={changed ? 'border-warning' : ''}
                              value={currentVal || ''}
                              options={(preview.accounts || []).map(a => ({ value: String(a.id), label: a.name }))}
                              onChange={val => {
                                if (val === String(s.suggestedAccount.id)) {
                                  setSuggestionOverrides(p => { const n = { ...p }; delete n[s.ingredient.id]; return n; });
                                } else {
                                  setSuggestionOverrides(p => ({ ...p, [s.ingredient.id]: val }));
                                }
                                // Changing the account implies the user wants this row applied.
                                setAcceptedSuggestions(p => ({ ...p, [s.ingredient.id]: true }));
                              }}
                            />
                          </td>
                          <td>
                            <span className={`badge ${s.confidence === 'high' ? 'bg-success' : s.confidence === 'medium' ? 'bg-warning text-dark' : 'bg-secondary'}`} style={{ fontSize: 10 }}>
                              {changed ? 'manual' : s.confidence}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </Section>

          {/* ── Manual match ── */}
          <Section
            title="Manual match"
            badge={preview.unmatched?.length || 0}
            badgeColor="warning"
            hint="No reasonable AI match found — select the QBO account for each, or leave as skip."
            defaultOpen={(preview.unmatched || []).length > 0}
          >
            {(preview.unmatched || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>All ingredients have a match.</p>
            ) : (
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead>
                  <tr><th>Ingredient</th><th>QBO account</th></tr>
                </thead>
                <tbody>
                  {preview.unmatched.map(u => (
                    <tr key={u.ingredient.id}>
                      <td>{u.ingredient.name}</td>
                      <td>
                        <SearchableSelect
                          size="sm"
                          style={{ maxWidth: 280 }}
                          placeholder="— skip —"
                          value={manualMatches[u.ingredient.id] || ''}
                          options={[
                            { value: '', label: '— skip —' },
                            ...(preview.accounts || []).map(a => ({ value: String(a.id), label: a.name })),
                          ]}
                          onChange={val => setManualMatches(p => ({ ...p, [u.ingredient.id]: val }))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* ── Apply ── */}
          <div className="mt-4 d-flex align-items-center gap-3">
            <button
              className="btn btn-primary"
              onClick={handleApply}
              disabled={applying || pendingCount === 0}
            >
              {applying
                ? <><span className="spinner-border spinner-border-sm me-2" />Applying…</>
                : `Apply Sync${pendingCount > 0 ? ` (${pendingCount} ingredient${pendingCount > 1 ? 's' : ''})` : ''}`}
            </button>
            {pendingCount === 0 && (
              <span className="text-muted" style={{ fontSize: 13 }}>Accept suggestions or choose manual matches to apply.</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Collapsible section wrapper ───────────────────────────────────────────────

function Section({ title, badge, badgeColor = 'secondary', hint, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card mb-3">
      <div
        className="card-header d-flex align-items-center gap-2"
        style={{ cursor: 'pointer', userSelect: 'none', fontSize: 14 }}
        onClick={() => setOpen(o => !o)}
      >
        <span className="fw-semibold">{title}</span>
        <span className={`badge bg-${badgeColor}`}>{badge}</span>
        {hint && <span className="text-muted ms-1" style={{ fontSize: 12, fontWeight: 400 }}>{hint}</span>}
        <span className="ms-auto text-muted" style={{ fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <div className="card-body">{children}</div>}
    </div>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────

export default function QboSyncPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'customers';

  const setTab = (t) => setSearchParams({ tab: t });

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 900 }}>
      <div className="d-flex align-items-center gap-3 mb-4">
        <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate('/admin/settings')}>
          ← Settings
        </button>
        <h4 className="mb-0">QuickBooks Online Sync</h4>
      </div>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link${tab === 'customers' ? ' active' : ''}`}
            onClick={() => setTab('customers')}
          >
            Customers
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${tab === 'products' ? ' active' : ''}`}
            onClick={() => setTab('products')}
          >
            Products
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${tab === 'ingredients' ? ' active' : ''}`}
            onClick={() => setTab('ingredients')}
          >
            Ingredients
          </button>
        </li>
      </ul>

      {tab === 'customers' && <CustomerSyncTab />}
      {tab === 'products' && <ProductSyncTab />}
      {tab === 'ingredients' && <IngredientAccountSyncTab />}
    </div>
  );
}
